"use client";

import { useState, useEffect, Suspense } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { GoogleOAuthProvider, GoogleLogin, CredentialResponse } from "@react-oauth/google";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Alert from "@mui/material/Alert";
import Divider from "@mui/material/Divider";
import Paper from "@mui/material/Paper";
import CircularProgress from "@mui/material/CircularProgress";
import Link from "next/link";
import { useAuth } from "@/contexts/AuthContext";
import type { CognitoChallenge } from "@/contexts/AuthContext";
import PasswordField from "@/components/PasswordField";

const GOOGLE_CLIENT_ID = process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID ?? "";

function LoginForm() {
  const { login, cognitoLogin, respondToChallenge, user, loading } = useAuth();
  const router = useRouter();
  const searchParams = useSearchParams();
  const reason = searchParams.get("reason");

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newPasswordConfirm, setNewPasswordConfirm] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [signingIn, setSigningIn] = useState(false);
  const [challenge, setChallenge] = useState<CognitoChallenge | null>(null);

  const isVidivetEmail = email.trim().toLowerCase().endsWith("@vidivet.com");
  const isCognitoFlow = email.trim().length > 0 && !isVidivetEmail;

  useEffect(() => {
    if (!loading && user) {
      if (user.mfaEnrolmentRequired && !user.totpEnrolled && user.identitySource === "cognito") {
        router.replace("/mfa-setup?returnTo=/");
      } else {
        router.replace("/");
      }
    }
  }, [loading, user, router]);

  const handleEmailChange = (val: string) => {
    setEmail(val);
    setError(null);
    setChallenge(null);
  };

  const handleGoogleSuccess = async (credentialResponse: CredentialResponse) => {
    if (!credentialResponse.credential) return;
    setSigningIn(true);
    setError(null);
    try {
      await login(credentialResponse.credential);
      router.replace("/");
    } catch (err: any) {
      if (err?.code === "DOMAIN_NOT_ALLOWED") {
        setError("Only @vidivet.com accounts are permitted.");
      } else if (err?.code === "ACCESS_NOT_PROVISIONED") {
        setError("Access not provisioned for this account. Contact your administrator.");
      } else {
        setError("Sign-in failed. Please try again.");
      }
    } finally {
      setSigningIn(false);
    }
  };

  const handleCognitoLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setSigningIn(true);
    setError(null);
    try {
      const result = await cognitoLogin(email.trim().toLowerCase(), password);
      if (result?.challenge) {
        setChallenge(result.challenge);
      }
    } catch (err: any) {
      if (err?.code === "INVALID_CREDENTIALS") {
        setError("Invalid email or password.");
      } else if (err?.code === "RATE_LIMITED") {
        setError("Too many login attempts. Please wait and try again.");
      } else if (err?.code === "ACCOUNT_DISABLED") {
        setError("Your account has been disabled. Contact support.");
      } else {
        setError("Sign-in failed. Please try again.");
      }
    } finally {
      setSigningIn(false);
    }
  };

  const handleChallengeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!challenge) return;
    if (newPassword !== newPasswordConfirm) {
      setError("Passwords do not match.");
      return;
    }
    setSigningIn(true);
    setError(null);
    try {
      await respondToChallenge(challenge.email, challenge.name, challenge.session, {
        NEW_PASSWORD: newPassword,
      });
    } catch (err: any) {
      setError("Failed to set new password. Please try again.");
    } finally {
      setSigningIn(false);
    }
  };

  const handleTotpChallengeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!challenge) return;
    setSigningIn(true);
    setError(null);
    try {
      await respondToChallenge(challenge.email, challenge.name, challenge.session, {
        SOFTWARE_TOKEN_MFA_CODE: totpCode,
      });
    } catch (err: any) {
      setError("Invalid code. Please check your authenticator app and try again.");
    } finally {
      setSigningIn(false);
    }
  };

  if (challenge?.name === "SOFTWARE_TOKEN_MFA") {
    return (
      <Paper elevation={2} sx={{ p: 4, width: "100%", maxWidth: 420 }}>
        <Typography variant="h5" sx={{ mb: 1, color: "primary.main" }}>
          Two-Factor Authentication
        </Typography>
        <Typography variant="body2" sx={{ mb: 3, color: "text.secondary" }}>
          Enter the 6-digit code from your authenticator app.
        </Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Box component="form" onSubmit={handleTotpChallengeSubmit}>
          <TextField
            label="6-digit code"
            type="text"
            inputMode="numeric"
            fullWidth
            size="small"
            value={totpCode}
            onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            sx={{ mb: 2 }}
            autoComplete="one-time-code"
            autoFocus
          />
          <Button type="submit" variant="contained" fullWidth disabled={signingIn || totpCode.length !== 6}>
            {signingIn ? <CircularProgress size={20} color="inherit" /> : "Verify"}
          </Button>
        </Box>
      </Paper>
    );
  }

  if (challenge?.name === "NEW_PASSWORD_REQUIRED") {
    return (
      <Paper elevation={2} sx={{ p: 4, width: "100%", maxWidth: 420 }}>
        <Typography variant="h5" sx={{ mb: 1, color: "primary.main" }}>
          Set Your Password
        </Typography>
        <Typography variant="body2" sx={{ mb: 3, color: "text.secondary" }}>
          You must set a new password to continue.
        </Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Box component="form" onSubmit={handleChallengeSubmit}>
          <PasswordField
            label="New password"
            fullWidth
            size="small"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            sx={{ mb: 2 }}
            autoComplete="new-password"
            helperText="At least 12 characters with uppercase, lowercase, number and symbol"
          />
          <PasswordField
            label="Confirm new password"
            fullWidth
            size="small"
            value={newPasswordConfirm}
            onChange={(e) => setNewPasswordConfirm(e.target.value)}
            sx={{ mb: 2 }}
            autoComplete="new-password"
          />
          <Button type="submit" variant="contained" fullWidth disabled={signingIn || !newPassword || !newPasswordConfirm}>
            {signingIn ? <CircularProgress size={20} color="inherit" /> : "Set Password & Sign In"}
          </Button>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper elevation={2} sx={{ p: 4, width: "100%", maxWidth: 420 }}>
      <Typography variant="h5" sx={{ mb: 1, color: "primary.main" }}>
        VidiVet Partner Portal
      </Typography>

      {reason === "idle" && (
        <Alert severity="info" sx={{ mb: 2 }}>
          You were signed out due to inactivity.
        </Alert>
      )}
      {reason === "session_expired" && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Your session expired. Please sign in again.
        </Alert>
      )}
      {reason === "invite_accepted" && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Your account is active. Please sign in.
        </Alert>
      )}
      {reason === "password_reset" && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Your password has been reset. Please sign in with your new password.
        </Alert>
      )}
      {reason === "email_verified" && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Email verified. You can now sign in with your new email address.
        </Alert>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <TextField
        label="Email address"
        type="email"
        fullWidth
        size="small"
        value={email}
        onChange={(e) => handleEmailChange(e.target.value)}
        sx={{ mb: 2 }}
        autoComplete="email"
      />

      {isVidivetEmail && (
        <>
          <Typography variant="body2" sx={{ mb: 1, color: "text.secondary" }}>
            VidiVet staff — continue with Google
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
            <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={() => setError("Google sign-in failed. Please try again.")}
                login_hint={email}
                useOneTap={false}
              />
            </GoogleOAuthProvider>
          </Box>
          {signingIn && (
            <Typography variant="body2" sx={{ color: "text.secondary", textAlign: "center" }}>
              Signing in…
            </Typography>
          )}
        </>
      )}

      {isCognitoFlow && (
        <>
          <Divider sx={{ my: 2 }} />
          <Box component="form" onSubmit={handleCognitoLogin}>
            <PasswordField
              label="Password"
              fullWidth
              size="small"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              sx={{ mb: 1 }}
              autoComplete="current-password"
            />
            <Box sx={{ textAlign: "right", mb: 2 }}>
              <Link href="/forgot-password" style={{ fontSize: "0.8rem", color: "inherit" }}>
                Forgot password?
              </Link>
            </Box>
            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={signingIn || !password}
            >
              {signingIn ? <CircularProgress size={20} color="inherit" /> : "Sign In"}
            </Button>
          </Box>
        </>
      )}
    </Paper>
  );
}

export default function LoginPage() {
  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "background.default",
        p: 2,
      }}
    >
      <Suspense>
        <LoginForm />
      </Suspense>
    </Box>
  );
}
